import { useState } from 'react';
import { Text, Card, Button, Input, Table, EmptyState, Badge } from '@teals/ui-web';
import type { Column } from '@teals/ui-web';
import { Link } from 'react-router-dom';
import {
  useGetOrgMembersQuery,
  useSearchStaffOrganisationsQuery,
  type OrgMemberItem,
  type OrgSearchItem,
} from '../../store/api/staffApi';

const orgColumns: Column<OrgSearchItem>[] = [
  { key: 'name', header: 'Organisation' },
  { key: 'status', header: 'Status' },
  {
    key: 'memberCount',
    header: 'Members',
    render: (value) => String(value ?? 0),
  },
];

const memberColumns: Column<OrgMemberItem>[] = [
  {
    key: 'name',
    header: 'Name',
    render: (value) => (value as string) || '—',
  },
  {
    key: 'email',
    header: 'Email',
    render: (value) => (value as string) || '—',
  },
  {
    key: 'role',
    header: 'Role',
    render: (value) =>
      value ? <Badge variant="neutral">{String(value)}</Badge> : '—',
  },
  { key: 'status', header: 'Status' },
];

export function OrgsPanel() {
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isFetching, isError } = useSearchStaffOrganisationsQuery({ q });
  const {
    data: members,
    isFetching: membersLoading,
    isError: membersError,
  } = useGetOrgMembersQuery(selectedId || '', { skip: !selectedId });
  const items = data?.items || [];
  const selected = items.find((item) => item.orgId === selectedId);

  return (
    <div style={{ marginTop: '1rem' }}>
      <Text variant="body2" style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
        Find an organisation and read its members. Delete a person on Users.
        Delete an organisation on <Link to="/super">Super User</Link>.
      </Text>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
        <Input
          style={{ flex: 1 }}
          placeholder="Search organisation…"
          value={q}
          onChange={(event) => setQ(event.target.value)}
          aria-label="Search organisation"
        />
      </div>
      {isError ? (
        <Card style={{ padding: '1rem' }}>
          <Text variant="body2">Could not load organisations.</Text>
        </Card>
      ) : null}
      {!isFetching && items.length === 0 ? (
        <EmptyState
          title="No organisations found"
          description="Try a different name or id."
        />
      ) : (
        <Table
          columns={[
            ...orgColumns,
            {
              key: 'orgId',
              header: '',
              render: (_value, row) => (
                <Button
                  size="sm"
                  variant={selectedId === row.orgId ? 'primary' : 'secondary'}
                  onClick={() => setSelectedId(row.orgId)}
                >
                  Members
                </Button>
              ),
            },
          ]}
          data={items}
          rowKey="orgId"
          loading={isFetching}
        />
      )}
      {selectedId ? (
        <Card style={{ padding: '1.25rem', marginTop: '1.25rem' }}>
          <Text variant="h4">{members?.name || selected?.name || 'Members'}</Text>
          {membersLoading ? <Text variant="body2">Loading members…</Text> : null}
          {membersError ? (
            <Text variant="body2">Could not load members.</Text>
          ) : !membersLoading && (members?.items || []).length === 0 ? (
            <EmptyState title="No members" description="This organisation has no members." />
          ) : (
            <Table
              columns={memberColumns}
              data={members?.items || []}
              rowKey="memberId"
              loading={membersLoading}
            />
          )}
        </Card>
      ) : null}
    </div>
  );
}
